/**
 * Razzmatazz film desk — platform-admin video production.
 * Loads catalog/treatment/script from gateway (not public static files).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { MARKETING_STUDIO_COPY, storedUiLanguage } from './i18n/consoleChromeCopy';
import './MarketingStudio.css';

type StudioVideo = {
  id: string;
  title: string;
  plan?: string;
  status?: string;
  funnelStage?: string;
  formatLabel?: string;
  videoPath?: string;
  scriptPath?: string;
  notes?: string;
  cta?: string;
  ctaRoute?: string;
  buyerQuestion?: string;
  hasTreatment?: boolean;
  hasStoryboard?: boolean;
  hasScript?: boolean;
};

type TreatmentBeat = {
  id: string;
  act?: string;
  start: number;
  end: number;
  vo?: string;
  mood?: string;
  layout?: string;
  visual_intent?: string;
  on_screen_text?: string;
  asset?: string;
  camera?: string;
  subbeats?: Array<{ at: number; asset?: string; on_screen_text?: string }>;
};

type Treatment = {
  id?: string;
  title?: string;
  method?: string;
  brands_on_screen?: string[];
  brands_spoken?: Record<string, string>;
  pacing_rules?: string[];
  beats?: TreatmentBeat[];
};

const inferGatewayBaseUrl = (): string => {
  const configured = (process.env.REACT_APP_GATEWAY_URL || '').trim();
  if (configured) {
    if (/^https?:\/\/localhost(?::|\/|$)/i.test(configured)) {
      return configured.replace(/localhost/i, '127.0.0.1').replace(/\/$/, '');
    }
    return configured.replace(/\/$/, '');
  }
  if (typeof window === 'undefined') return 'http://127.0.0.1:8000';
  const host = window.location.hostname;
  const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1';
  if (isLocal) return 'http://127.0.0.1:8000';
  const apexHost = host.replace(/^www\./, '').replace(/^dashboard\./, '').replace(/^metrics\./, '');
  return `https://${apexHost}`;
};

const GATEWAY = inferGatewayBaseUrl();
const PUBLIC_VIDEO_BASE = '/videos/implementation-readiness';

function expandBeats(beats: TreatmentBeat[]): TreatmentBeat[] {
  return beats
    .flatMap((b) => {
      if (!b.subbeats?.length) return [b];
      const ends = [...b.subbeats.slice(1).map((s) => s.at), b.end];
      return b.subbeats.map((s, i) => ({
        ...b,
        id: `${b.id}_${i + 1}`,
        start: s.at,
        end: ends[i],
        on_screen_text: s.on_screen_text || b.on_screen_text,
        asset: s.asset || b.asset,
      }));
    })
    .sort((a, b) => a.start - b.start);
}

const MarketingStudioPage: React.FC = () => {
  const chrome = MARKETING_STUDIO_COPY[storedUiLanguage()];
  const { accessToken } = useAuth();
  const [videos, setVideos] = React.useState<StudioVideo[]>([]);
  const [selectedId, setSelectedId] = React.useState<string>('');
  const [treatment, setTreatment] = React.useState<Treatment | null>(null);
  const [scriptMd, setScriptMd] = React.useState<string>('');
  const [storyboard, setStoryboard] = React.useState<unknown>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [detailLoading, setDetailLoading] = React.useState(false);
  const [tab, setTab] = React.useState<'animatic' | 'script' | 'preview'>('animatic');
  const [clock, setClock] = React.useState(0);
  const [playing, setPlaying] = React.useState(false);
  const [ideas, setIdeas] = React.useState<Record<string, { provider?: string; idea?: Record<string, unknown>; error?: string }>>({});
  const [ideaBusyId, setIdeaBusyId] = React.useState<string | null>(null);
  const rafRef = React.useRef<number | null>(null);
  const playStartedRef = React.useRef(0);

  const authHeaders = React.useMemo(
    () => ({ Authorization: `Bearer ${accessToken || ''}` }),
    [accessToken],
  );

  const generateVisualIdea = React.useCallback(
    async (beat: TreatmentBeat) => {
      if (!accessToken || !selectedId) return;
      setIdeaBusyId(beat.id);
      try {
        const res = await fetch(`${GATEWAY}/api/v1/admin/marketing-studio/visual-idea`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            video_id: selectedId,
            beat_id: beat.id,
            on_screen_text: beat.on_screen_text,
            vo: beat.vo,
            mood: beat.mood,
            act: beat.act,
            visual_intent: beat.visual_intent,
          }),
        });
        if (!res.ok) {
          throw new Error(res.status === 403 ? 'Platform admin required.' : `Idea failed (${res.status})`);
        }
        const data = (await res.json()) as { provider?: string; idea?: Record<string, unknown> };
        setIdeas((prev) => ({ ...prev, [beat.id]: { provider: data.provider, idea: data.idea } }));
      } catch (err) {
        setIdeas((prev) => ({
          ...prev,
          [beat.id]: { error: err instanceof Error ? err.message : String(err) },
        }));
      } finally {
        setIdeaBusyId(null);
      }
    },
    [accessToken, authHeaders, selectedId],
  );

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!accessToken) {
        setError('Sign in required.');
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`${GATEWAY}/api/v1/admin/marketing-studio/catalog`, {
          headers: authHeaders,
        });
        if (!res.ok) {
          throw new Error(res.status === 403 ? 'Platform admin required.' : `Catalog failed (${res.status})`);
        }
        const data = (await res.json()) as { videos?: StudioVideo[] };
        if (cancelled) return;
        const list = data.videos || [];
        setVideos(list);
        setSelectedId((prev) => prev || list[0]?.id || '');
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, authHeaders]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!accessToken || !selectedId) return;
      setDetailLoading(true);
      setTreatment(null);
      setScriptMd('');
      setStoryboard(null);
      setClock(0);
      setPlaying(false);
      try {
        const base = `${GATEWAY}/api/v1/admin/marketing-studio/videos/${encodeURIComponent(selectedId)}`;
        const [tRes, sRes, sbRes] = await Promise.all([
          fetch(`${base}/treatment`, { headers: authHeaders }),
          fetch(`${base}/script`, { headers: authHeaders }),
          fetch(`${base}/storyboard`, { headers: authHeaders }),
        ]);
        if (cancelled) return;
        if (tRes.ok) setTreatment((await tRes.json()) as Treatment);
        if (sRes.ok) {
          const body = (await sRes.json()) as { markdown?: string };
          setScriptMd(body.markdown || '');
        }
        if (sbRes.ok) setStoryboard(await sbRes.json());
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [accessToken, authHeaders, selectedId]);

  const selected = videos.find((v) => v.id === selectedId) || null;
  const beats = React.useMemo(() => expandBeats(treatment?.beats || []), [treatment]);
  const total = beats.length ? beats[beats.length - 1].end : 0;

  React.useEffect(() => {
    if (!playing) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return undefined;
    }
    playStartedRef.current = performance.now() - clock * 1000;
    const tick = (now: number) => {
      const t = Math.min(total, (now - playStartedRef.current) / 1000);
      setClock(t);
      if (t < total) rafRef.current = requestAnimationFrame(tick);
      else setPlaying(false);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // Intentionally omit `clock` — only seed playhead when Play is pressed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, total]);

  const videoSrc = selected?.videoPath
    ? `${PUBLIC_VIDEO_BASE}/${selected.videoPath.replace(/^\//, '')}`
    : '';

  return (
    <section className="mstudio" aria-label={chrome.title}>
      <header className="mstudio__hero">
        <div>
          <p className="mstudio__eyebrow">{chrome.eyebrow}</p>
          <h1>{chrome.title}</h1>
          <p className="mstudio__lede">
            {chrome.lede}
          </p>
        </div>
        <div className="mstudio__gates">
          <span>1 Brief / script</span>
          <span>2 Animatic lock</span>
          <span>3 Produce</span>
          <span>4 Publish MP4</span>
        </div>
      </header>

      {error ? (
        <div className="mstudio__error" role="alert">
          {error}
        </div>
      ) : null}
      {loading ? <p className="mstudio__muted">Loading catalog…</p> : null}

      <div className="mstudio__layout">
        <aside className="mstudio__list" aria-label="Video catalog">
          {videos.map((v) => (
            <button
              key={v.id}
              type="button"
              className={`mstudio__card${v.id === selectedId ? ' is-active' : ''}`}
              onClick={() => setSelectedId(v.id)}
            >
              <strong>{v.title || v.id}</strong>
              <span>
                {v.plan || '—'} · {v.status || 'draft'}
              </span>
              <span className="mstudio__meta">
                {v.hasTreatment ? 'treatment' : 'no treatment'}
                {v.hasScript ? ' · script' : ''}
              </span>
            </button>
          ))}
          {!loading && !videos.length ? <p className="mstudio__muted">No videos in catalog.</p> : null}
        </aside>

        <div className="mstudio__detail">
          {!selected ? (
            <p className="mstudio__muted">Select a video.</p>
          ) : (
            <>
              <div className="mstudio__detail-head">
                <div>
                  <h2>{selected.title}</h2>
                  <p className="mstudio__muted">
                    {selected.formatLabel || selected.funnelStage || '—'}
                    {selected.buyerQuestion ? ` · ${selected.buyerQuestion}` : ''}
                  </p>
                </div>
                <div className="mstudio__actions">
                  {selected.ctaRoute ? (
                    <Link className="mstudio__btn ghost" to={selected.ctaRoute}>
                      Plan page
                    </Link>
                  ) : null}
                  {videoSrc ? (
                    <a className="mstudio__btn" href={videoSrc} target="_blank" rel="noreferrer">
                      Open published MP4
                    </a>
                  ) : null}
                </div>
              </div>

              {selected.cta ? (
                <p className="mstudio__cta-line">
                  CTA: {selected.cta}
                  {selected.ctaRoute ? (
                    <>
                      {' '}
                      · <Link to={selected.ctaRoute}>{selected.ctaRoute}</Link>
                    </>
                  ) : null}
                </p>
              ) : null}

              <div className="mstudio__tabs" role="tablist">
                {(['animatic', 'script', 'preview'] as const).map((id) => (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={tab === id}
                    className={tab === id ? 'is-active' : ''}
                    onClick={() => setTab(id)}
                  >
                    {id === 'animatic' ? 'Animatic' : id === 'script' ? 'Script' : 'Preview'}
                  </button>
                ))}
              </div>

              {detailLoading ? <p className="mstudio__muted">Loading authoring assets…</p> : null}

              {tab === 'animatic' ? (
                <div className="mstudio__animatic">
                  {!treatment?.beats?.length ? (
                    <p className="mstudio__muted">
                      No treatment yet for this video. Add{' '}
                      <code>{'{id}'}.treatment.json</code> under marketing-studio/scripts.
                    </p>
                  ) : (
                    <>
                      <div className="mstudio__brands">
                        {(treatment.brands_on_screen || []).map((b) => (
                          <span key={b}>
                            <b>{b}</b> → “{(treatment.brands_spoken || {})[b] || b}”
                          </span>
                        ))}
                      </div>
                      <ul className="mstudio__rules">
                        {(treatment.pacing_rules || []).map((r) => (
                          <li key={r}>{r}</li>
                        ))}
                      </ul>
                      <div className="mstudio__timeline">
                        <div className="mstudio__clock">
                          {clock.toFixed(1)}s / {total.toFixed(1)}s
                        </div>
                        <button type="button" className="mstudio__btn" onClick={() => setPlaying(true)} disabled={!total}>
                          Play timeline
                        </button>
                        <button
                          type="button"
                          className="mstudio__btn ghost"
                          onClick={() => {
                            setPlaying(false);
                            setClock(0);
                          }}
                        >
                          Stop
                        </button>
                      </div>
                      <div className="mstudio__beats">
                        {beats.map((b) => {
                          const on = clock >= b.start && clock < b.end;
                          return (
                            <article
                              key={b.id}
                              className={`mstudio__beat act-${b.act || 'workflow'}${on ? ' is-live' : ''}`}
                            >
                              <header>
                                <span>{b.act || 'beat'}</span>
                                <span>
                                  {b.start.toFixed(1)}–{b.end.toFixed(1)}s
                                </span>
                              </header>
                              <h3>{b.on_screen_text || b.id}</h3>
                              <p>
                                <b>Mood</b> {b.mood || '—'} · <b>Layout</b> {b.layout || '—'}
                              </p>
                              <p>{b.visual_intent}</p>
                              <p className="mstudio__vo">
                                <b>VO</b> {b.vo}
                              </p>
                              <p className="mstudio__meta">
                                asset: <code>{b.asset}</code>
                              </p>
                              <div className="mstudio__idea-row">
                                <button
                                  type="button"
                                  className="mstudio__btn ghost"
                                  disabled={ideaBusyId === b.id}
                                  onClick={() => generateVisualIdea(b)}
                                >
                                  {ideaBusyId === b.id ? 'Inventing…' : 'Invent visual idea'}
                                </button>
                              </div>
                              {ideas[b.id]?.error ? (
                                <p className="mstudio__error">{ideas[b.id].error}</p>
                              ) : null}
                              {ideas[b.id]?.idea ? (
                                <div className="mstudio__idea">
                                  <p className="mstudio__meta">AI · {ideas[b.id].provider || '—'}</p>
                                  <h4>{String(ideas[b.id].idea?.concept_title || 'Idea')}</h4>
                                  <p>{String(ideas[b.id].idea?.metaphor || '')}</p>
                                  <p>
                                    <b>Composition</b> {String(ideas[b.id].idea?.composition || '')}
                                  </p>
                                  <p>
                                    <b>Motion</b> {String(ideas[b.id].idea?.motion || '')}
                                  </p>
                                  <p>
                                    <b>Image prompt</b> {String(ideas[b.id].idea?.image_prompt || '')}
                                  </p>
                                </div>
                              ) : null}
                            </article>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              ) : null}

              {tab === 'script' ? (
                <pre className="mstudio__script">{scriptMd || 'No markdown script on file.'}</pre>
              ) : null}

              {tab === 'preview' ? (
                <div className="mstudio__preview">
                  {videoSrc ? (
                    <video key={videoSrc} controls playsInline src={videoSrc} />
                  ) : (
                    <p className="mstudio__muted">No published MP4 yet.</p>
                  )}
                  {selected.notes ? <p className="mstudio__notes">{selected.notes}</p> : null}
                  {storyboard ? (
                    <details>
                      <summary>Raw storyboard JSON</summary>
                      <pre>{JSON.stringify(storyboard, null, 2)}</pre>
                    </details>
                  ) : null}
                </div>
              ) : null}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default MarketingStudioPage;
